import { apiClient } from '../lib/api';
import type { LoginCredentials, User, UserRole } from '../types/auth';

interface LoginResponseDto {
  accessToken: string;
  refreshToken: string;
  user: {
    id: string;
    email: string;
    fullName: string | null;
    role: string;
    roles?: string[];
    labs: { id: string; name: string; roleInLab: string | null }[];
    defaultLabId: string | null;
    guideAcknowledged: boolean;
  };
}

export interface LoginResponse {
  accessToken: string;
  refreshToken: string;
  user: User;
}

function mapUser(dto: LoginResponseDto['user']): User {
  const role = dto.role as UserRole;
  const roles = (dto.roles?.length ? dto.roles : [dto.role]) as UserRole[];
  return {
    id: dto.id,
    email: dto.email,
    fullName: dto.fullName,
    role,
    roles,
    primaryRole: role,
    labs: dto.labs,
    defaultLabId: dto.defaultLabId,
    guideAcknowledged: dto.guideAcknowledged,
  };
}

interface ApiError {
  message: string;
}

export const authService = {
  async login(credentials: LoginCredentials): Promise<LoginResponse> {
    // Don't use apiClient for login - it has no token yet
    // Also prevents interceptor interference
    const response = await apiClient.post<LoginResponseDto>(
      '/api/auth/login',
      credentials,
      {
        // Skip the request interceptor for auth endpoints
        headers: { Authorization: undefined },
      }
    );
    const data = response.data;
    return { accessToken: data.accessToken, refreshToken: data.refreshToken, user: mapUser(data.user) };
  },

  async refresh(refreshToken: string): Promise<LoginResponse> {
    // Use apiClient but skip auth header to avoid interceptor loops
    const response = await apiClient.post<LoginResponseDto>(
      '/api/auth/refresh',
      { refreshToken },
      {
        headers: { Authorization: undefined },
      }
    );
    const data = response.data;
    return { accessToken: data.accessToken, refreshToken: data.refreshToken, user: mapUser(data.user) };
  },

  async logout(refreshToken: string): Promise<void> {
    await apiClient.post('/api/auth/logout', { refreshToken });
  },

  async switchLab(labId: string): Promise<LoginResponse> {
    const response = await apiClient.post<LoginResponseDto>('/api/auth/switch-lab', { labId });
    const data = response.data;
    return { accessToken: data.accessToken, refreshToken: data.refreshToken, user: mapUser(data.user) };
  },

  async acknowledgeGuide(): Promise<void> {
    await apiClient.post('/api/v1/users/me/acknowledge-guide');
  },
};

export function isApiError(
  error: unknown
): error is { response: { data: ApiError } } {
  return (
    typeof error === 'object' &&
    error !== null &&
    'response' in error &&
    typeof (error as { response?: { data?: { message?: unknown } } }).response
      ?.data?.message === 'string'
  );
}
